import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
  useReducer,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tables, TablesUpdate } from "@/types/database.types";
import { useToast } from "@/hooks/use-toast";

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export type Organization = Tables<"organizations">;
export type OrganizationUpdate = TablesUpdate<"organizations">;

// Sensitive fields that should not be updated by regular users
type SensitiveOrganizationFields =
  | "stripe_customer_id"
  | "stripe_subscription_id"
  | "subscription_status"
  | "is_super_admin"
  | "current_period_end"
  | "active_agents_count"
  | "conversations_this_month"
  | "conversations_reset_date"
  | "files_count"
  | "knowledge_items_count"
  | "storage_usage_mb"
  | "plan_id";

export type SafeOrganizationUpdate = Omit<
  OrganizationUpdate,
  SensitiveOrganizationFields
>;

export interface OrganizationState {
  organization: Organization | null;
  isLoading: boolean;
  error: Error | null;
  lastUpdated: number | null;
}

export interface OrganizationActions {
  setOrganization: (organization: Organization | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  reset: () => void;
  update: (updates: Partial<Organization>) => void;
}

export interface OrganizationContextValue extends OrganizationState {
  // Data access
  agents?: Array<{ id: string; agent_name: string; title?: string }>;
  composioConnections?: Array<{
    id: string;
    tool: string;
    connection_id: string;
    is_active: boolean;
    connection_data?: any;
  }>;

  // Actions
  refreshOrganization: () => Promise<void>;
  updateOrganization: (updates: SafeOrganizationUpdate) => Promise<void>;
  refreshComposioConnections: () => Promise<void>;

  // State helpers
  hasPermission: (feature: string) => boolean;
  isFeatureEnabled: (feature: string) => boolean;

  // Usage & limits
  getUsageStats: () => {
    agents: number;
    conversations: number;
    storage: number;
    files: number;
    knowledgeItems: number;
  };

  // Real-time subscriptions
  subscribeToUpdates: (callback: (org: Organization) => void) => () => void;
}

export interface OrganizationProviderProps {
  children: ReactNode;
  organizationId?: string | null;
  onError?: (error: Error) => void;
  initialData?: Organization | null;
  enableRealtime?: boolean;
}

// =============================================================================
// REDUCER FOR STATE MANAGEMENT
// =============================================================================

type OrganizationAction =
  | { type: "SET_ORGANIZATION"; payload: Organization | null }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: Error | null }
  | { type: "UPDATE_ORGANIZATION"; payload: Partial<Organization> }
  | { type: "RESET" };

const initialState: OrganizationState = {
  organization: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

function organizationReducer(
  state: OrganizationState,
  action: OrganizationAction,
): OrganizationState {
  switch (action.type) {
    case "SET_ORGANIZATION":
      return {
        ...state,
        organization: action.payload,
        isLoading: false,
        error: null,
        lastUpdated: action.payload ? Date.now() : null,
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
        error: action.payload ? null : state.error, // Clear error when starting new load
      };
    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    case "UPDATE_ORGANIZATION":
      if (!state.organization) return state;
      return {
        ...state,
        organization: { ...state.organization, ...action.payload },
        lastUpdated: Date.now(),
      };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

// =============================================================================
// DEFAULT CONTEXT VALUE (for error boundaries)
// =============================================================================

const defaultContextValue: OrganizationContextValue = {
  organization: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
  agents: [],

  refreshOrganization: async () => {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider",
    );
  },
  updateOrganization: async () => {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider",
    );
  },
  hasPermission: () => false,
  isFeatureEnabled: () => false,
  getUsageStats: () => ({
    agents: 0,
    conversations: 0,
    storage: 0,
    files: 0,
    knowledgeItems: 0,
  }),
  subscribeToUpdates: () => () => {},
};

// =============================================================================
// CONTEXT CREATION
// =============================================================================

const OrganizationContext =
  createContext<OrganizationContextValue>(defaultContextValue);

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

export function OrganizationProvider({
  children,
  organizationId,
  onError,
  initialData = null,
  enableRealtime = true,
}: OrganizationProviderProps) {
  const { toast } = useToast();
  const [state, dispatch] = useReducer(organizationReducer, {
    ...initialState,
    organization: initialData,
  });

  // Track organization ID changes to prevent unnecessary effects
  const prevOrganizationIdRef = useRef<string | null | undefined>(
    organizationId,
  );
  const realtimeChannelRef = useRef<any>(null);
  const mountedRef = useRef(true);

  // Agents state (could be moved to separate provider if it grows)
  const [agents, setAgents] = useState<
    Array<{ id: string; agent_name: string; title?: string }>
  >([]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
  }, []);

  // =============================================================================
  // CORE DATA FETCHING
  // =============================================================================

  const fetchOrganization = useCallback(
    async (orgId: string): Promise<Organization | null> => {
      try {
        const { data, error } = await supabase
          .from("organizations")
          .select("*")
          .eq("id", orgId)
          .single();

        if (error) {
          throw new Error(`Failed to fetch organization: ${error.message}`);
        }

        return data;
      } catch (error) {
        console.error("Error fetching organization:", error);
        throw error;
      }
    },
    [],
  );

  const fetchAgents = useCallback(async (orgId: string) => {
    try {
      const { data, error } = await supabase
        .from("agents")
        .select("id, agent_name, title")
        .eq("organization_id", orgId)
        .eq("archived", false);

      if (error) {
        console.error("Error fetching agents:", error);
        return;
      }

      if (mountedRef.current) {
        setAgents(data || []);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  }, []);

  // =============================================================================
  // EXPOSED ACTIONS
  // =============================================================================

  const refreshOrganization = useCallback(async (): Promise<void> => {
    if (!organizationId) {
      dispatch({ type: "SET_ORGANIZATION", payload: null });
      return;
    }

    dispatch({ type: "SET_LOADING", payload: true });

    try {
      const org = await fetchOrganization(organizationId);

      if (mountedRef.current) {
        dispatch({ type: "SET_ORGANIZATION", payload: org });

        // Fetch related data
        if (org) {
          await fetchAgents(org.id);
        }
      }
    } catch (error) {
      const err =
        error instanceof Error ? error : new Error("Unknown error occurred");

      if (mountedRef.current) {
        dispatch({ type: "SET_ERROR", payload: err });
        onError?.(err);
      }
    }
  }, [organizationId, fetchOrganization, fetchAgents, onError]);

  const updateOrganization = useCallback(
    async (updates: SafeOrganizationUpdate): Promise<void> => {
      if (!state.organization) {
        throw new Error("No organization to update");
      }

      try {
        const { error } = await supabase
          .from("organizations")
          .update(updates)
          .eq("id", state.organization.id);

        if (error) {
          throw new Error(`Failed to update organization: ${error.message}`);
        }

        // Optimistically update local state
        if (mountedRef.current) {
          dispatch({ type: "UPDATE_ORGANIZATION", payload: updates });
        }

        toast({
          title: "Organization updated",
          description: "Your changes have been saved successfully.",
        });
      } catch (error) {
        const err = error instanceof Error ? error : new Error("Update failed");

        toast({
          variant: "destructive",
          title: "Update failed",
          description: err.message,
        });

        throw err;
      }
    },
    [state.organization, toast],
  );

  // =============================================================================
  // HELPER FUNCTIONS
  // =============================================================================

  const hasPermission = useCallback(
    (feature: string): boolean => {
      if (!state.organization) return false;

      // Check if user is super admin
      if (state.organization.is_super_admin) return true;

      // Check feature flags
      const features =
        (state.organization.features as Record<string, boolean>) || {};
      return features[feature] === true;
    },
    [state.organization],
  );

  const isFeatureEnabled = useCallback(
    (feature: string): boolean => {
      if (!state.organization) return false;

      const features =
        (state.organization.features as Record<string, boolean>) || {};
      return features[feature] === true;
    },
    [state.organization],
  );

  const getUsageStats = useCallback(() => {
    if (!state.organization) {
      return {
        agents: 0,
        conversations: 0,
        storage: 0,
        files: 0,
        knowledgeItems: 0,
      };
    }

    return {
      agents: state.organization.active_agents_count || 0,
      conversations: state.organization.conversations_this_month || 0,
      storage: state.organization.storage_usage_mb || 0,
      files: state.organization.files_count || 0,
      knowledgeItems: state.organization.knowledge_items_count || 0,
    };
  }, [state.organization]);

  const subscribeToUpdates = useCallback(
    (callback: (org: Organization) => void) => {
      if (!state.organization) {
        return () => {};
      }

      const channel = supabase
        .channel(`org-${state.organization.id}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "organizations",
            filter: `id=eq.${state.organization.id}`,
          },
          (payload) => {
            const updatedOrg = payload.new as Organization;
            if (mountedRef.current) {
              dispatch({ type: "SET_ORGANIZATION", payload: updatedOrg });
              callback(updatedOrg);
            }
          },
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    },
    [state.organization],
  );

  // =============================================================================
  // EFFECTS
  // =============================================================================

  // Fetch organization when organizationId changes
  useEffect(() => {
    if (prevOrganizationIdRef.current !== organizationId) {
      prevOrganizationIdRef.current = organizationId;
      refreshOrganization();
    }
  }, [organizationId, refreshOrganization]);

  // Set up real-time subscription
  useEffect(() => {
    if (!enableRealtime || !state.organization) {
      return;
    }

    // Clean up existing channel
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
    }

    // Set up new channel
    realtimeChannelRef.current = supabase
      .channel(`org-updates-${state.organization.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "organizations",
          filter: `id=eq.${state.organization.id}`,
        },
        (payload) => {
          const updatedOrg = payload.new as Organization;
          if (mountedRef.current) {
            dispatch({ type: "SET_ORGANIZATION", payload: updatedOrg });
          }
        },
      )
      .subscribe();

    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
  }, [enableRealtime, state.organization?.id]);

  // =============================================================================
  // MEMOIZED CONTEXT VALUE
  // =============================================================================

  const contextValue: OrganizationContextValue = useMemo(
    () => ({
      // State
      organization: state.organization,
      isLoading: state.isLoading,
      error: state.error,
      lastUpdated: state.lastUpdated,

      // Related data
      agents,

      // Actions
      refreshOrganization,
      updateOrganization,

      // Helpers
      hasPermission,
      isFeatureEnabled,
      getUsageStats,
      subscribeToUpdates,
    }),
    [
      state.organization,
      state.isLoading,
      state.error,
      state.lastUpdated,
      agents,
      refreshOrganization,
      updateOrganization,
      hasPermission,
      isFeatureEnabled,
      getUsageStats,
      subscribeToUpdates,
    ],
  );

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
}

// =============================================================================
// CUSTOM HOOK
// =============================================================================

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);

  if (!context) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider. " +
        "Make sure to wrap your component tree with <OrganizationProvider>.",
    );
  }

  return context;
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for components that need organization data but should gracefully degrade
 * if no organization is available (useful for public components)
 */
export function useOrganizationOptional(): OrganizationContextValue | null {
  try {
    return useOrganization();
  } catch {
    return null;
  }
}

/**
 * Hook that throws if organization is not loaded or loading
 * Useful for components that absolutely require organization data
 */
export function useOrganizationRequired(): Required<
  Pick<OrganizationContextValue, "organization">
> &
  OrganizationContextValue {
  const org = useOrganization();

  if (org.isLoading) {
    throw new Error("Organization is still loading");
  }

  if (!org.organization) {
    throw new Error("Organization is required but not available");
  }

  return org as Required<Pick<OrganizationContextValue, "organization">> &
    OrganizationContextValue;
}

// =============================================================================
// TESTING UTILITIES
// =============================================================================

/**
 * Test wrapper that provides a mock organization context
 * Useful for unit tests and Storybook
 */
export function OrganizationProviderMock({
  children,
  organization = null,
  isLoading = false,
  error = null,
}: {
  children: ReactNode;
  organization?: Organization | null;
  isLoading?: boolean;
  error?: Error | null;
}) {
  const mockValue: OrganizationContextValue = {
    organization,
    isLoading,
    error,
    lastUpdated: organization ? Date.now() : null,
    agents: [],

    refreshOrganization: async () => {},
    updateOrganization: async () => {},
    hasPermission: () => true,
    isFeatureEnabled: () => true,
    getUsageStats: () => ({
      agents: 0,
      conversations: 0,
      storage: 0,
      files: 0,
      knowledgeItems: 0,
    }),
    subscribeToUpdates: () => () => {},
  };

  return (
    <OrganizationContext.Provider value={mockValue}>
      {children}
    </OrganizationContext.Provider>
  );
}
